import { isVideo, type TRecordingQuestion } from '@hubertai/screening-question-types';
import { Mic, RotateCcw, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface RecordingPreviewProps {
  question: TRecordingQuestion<string>;
}

/**
 * Capability card for an audio / video question.
 *
 * Deliberately NOT a preview of the recorder. The recorder is AddPipe-driven and lives in the
 * chat app; embedding it here would need an embed key, burn recorder sessions, and prompt the
 * recruiter for camera access — while showing only the consent copy and a button, which are
 * code-provided and not editable. Everything a recruiter CAN configure (duration bounds, retakes,
 * thinking time, whether an answer is required, video quality) is invisible in a live recorder, so this card
 * states those settings instead. That is the useful information for proofreading a template.
 *
 * The copy here is builder chrome, so it comes from manager's own i18n in the recruiter's UI
 * language and follows the recruiter's reading direction — unlike the shared question components,
 * which render candidate-facing content in the interview language and set `dir` from it.
 */
export const RecordingPreview = ({ question }: RecordingPreviewProps) => {
  const { t } = useTranslation('Questionsv2');

  const video = isVideo(question);
  const Icon = video ? Video : Mic;
  const {
    min_duration_seconds: min,
    max_duration_seconds: max,
    allow_retake,
    max_retakes,
    max_thinking_seconds,
    required,
  } = question;

  // Rendered numerically rather than as a sentence: "10–90s" needs no translation and reads the
  // same in every language, which also keeps the card compact.
  const duration =
    min != null && max != null ? `${min}–${max}s` : max != null ? `≤${max}s` : min != null ? `≥${min}s` : null;

  // max_retakes overrides allow_retake when set; 0 means none.
  const retakes = max_retakes ?? (allow_retake ? Infinity : 0);
  const retakeChip =
    retakes === 0
      ? t('questionPreview.recording.noRetakes')
      : retakes === Infinity
        ? t('questionPreview.recording.retakesAllowed')
        : t('questionPreview.recording.retakesUpTo', { count: retakes });

  const chips = [
    duration,
    retakeChip,
    max_thinking_seconds != null
      ? t('questionPreview.recording.thinkingTime', { seconds: max_thinking_seconds })
      : null,
    required ? t('questionPreview.recording.required') : t('questionPreview.recording.optional'),
    video && question.quality ? question.quality : null,
  ].filter(Boolean) as string[];

  return (
    <div className="flex max-w-md flex-col gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
      <div className="flex items-center gap-2">
        <Icon size={16} className="shrink-0 text-violet-600" />
        <span className="text-xs font-semibold text-slate-700">
          {video ? t('questionPreview.recording.videoAnswer') : t('questionPreview.recording.audioAnswer')}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {chips.map((chip) => (
          <span
            key={chip}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600"
          >
            {chip === retakeChip && retakes > 0 ? <RotateCcw size={11} /> : null}
            {chip}
          </span>
        ))}
      </div>

      <p className="text-[11px] text-slate-400">{t('questionPreview.recording.notPreviewable')}</p>
    </div>
  );
};
